import moment from 'moment'
import cn from 'classnames'
import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { Sorter } from '../../../../components/Sorter/Sorter'
import { Dropdown } from '../../../../components/Dropdown'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { LeadByIndicator } from '../../../../components/LeadByIndicator'
import {
  FINANCIAL_STATUS_TYPES_TO_CLASSNAME,
  Status
} from '../../../../components/Status'
import { currencyFormatter } from '../../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { optionFormatter } from '../../../Contracts/helpers'
import {
  getHSFilterOptions,
  getPayorFilterOptions,
  getVendorFilterOptions
} from './api'
import { FINANCIAL_STATUS_LABEL } from '../../constants'
import { timelineDateFormat } from '../../../../components/Timelines/constants'
import { ReactComponent as FilterApplied16 } from '../../../../assets/svg/FilterApplied16.svg'
import { ReactComponent as Filter16 } from '../../../../assets/svg/Filter16.svg'
import {
  CognusAdminData,
  CognusAdminDataFilters,
  CognusAdminFilterFunc,
  CognusAdminTableColumnsFunc
} from './types'
import { DueDateStatuses } from '../../types'
import { DescriptionCell } from '../../../../components/DescriptionCell/DescriptionCell'
import { SelectPayorRecipientPopoverContent } from './SelectPayorRecipientPopoverContent'
import { Typography } from '../../../../components/Typography'
import { checkShowPayorRecipientPopover } from './utils'
import { ReactComponent as Download16 } from '../../../../assets/svg/Download16.svg'
import { ROLES } from '../../../Permission'

export const columns = ({
  hideHSName,
  isReceivableTab,
  canSeeInvoices,
  applyFilters,
  filters,
  generateDocument,
  userRole
}: CognusAdminTableColumnsFunc): ColumnsType<CognusAdminData> => {
  const cols: ColumnsType<CognusAdminData> = []

  if (!hideHSName) {
    cols.push({
      dataIndex: 'health_system_name',
      key: 'health_system_name',
      className: 'vertical-align-middle',
      width: 250,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CognusAdminData>
          dataIndex="health_system_name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Health System
        </Sorter>
      ),
      render: (_, { health_system_name }) => health_system_name,
      sorter: true,
      showSorterTooltip: false,
      filterDropdown: (props) => (
        <Dropdown.Filter<CognusAdminDataFilters, CognusAdminFilterFunc, unknown>
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={getHSFilterOptions}
          field="health_systems"
          optionFormatter={optionFormatter}
          asyncSearch
          {...props}
        />
      ),
      filterIcon: filters?.health_systems?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true
    })
  }

  if (isReceivableTab) {
    cols.push({
      dataIndex: 'led_by',
      key: 'led_by',
      className: 'vertical-align-middle',
      width: 106,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<CognusAdminData>
          dataIndex="led_by"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Led by
        </Sorter>
      ),
      render: (_, { led_by, name }) =>
        led_by ? <LeadByIndicator tooltipTitle={name} value={led_by} /> : '-',
      sorter: true,
      showSorterTooltip: false
    })
  }

  cols.push({
    dataIndex: 'vendor',
    key: 'vendor',
    className: 'vertical-align-middle',
    width: 136,
    title: ({ sortOrder, sortColumn }) => (
      <Sorter<CognusAdminData>
        dataIndex="vendor"
        sortColumn={sortColumn}
        sortOrder={sortOrder as SortOrder}
      >
        Vendor
      </Sorter>
    ),
    render: (_, { vendor }) => vendor.name,
    sorter: true,
    showSorterTooltip: false,
    filterDropdown: (props) => (
      <Dropdown.Filter<CognusAdminDataFilters, CognusAdminFilterFunc, unknown>
        applyFilters={applyFilters}
        appliedFilters={filters}
        searchCallback={getVendorFilterOptions}
        field="vendors"
        optionFormatter={optionFormatter}
        asyncSearch
        {...props}
      />
    ),
    filterIcon: filters?.vendors?.length ? <FilterApplied16 /> : <Filter16 />,
    filterSearch: true
  })

  return cols.concat(
    [
      {
        dataIndex: 'period_ending',
        key: 'period_ending',
        className: 'vertical-align-middle',
        width: 158,
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<CognusAdminData>
            dataIndex="period_ending"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Period ending
          </Sorter>
        ),
        render: (_, { period_ending }) => (
          <span className="separate-item">
            {period_ending
              ? moment(period_ending).format(timelineDateFormat)
              : '-'}
          </span>
        ),
        sorter: true,
        showSorterTooltip: false
      },
      {
        dataIndex: 'due_date',
        key: 'due_date',
        className: 'vertical-align-middle',
        width: 158,
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<CognusAdminData>
            dataIndex="due_date"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Due date
          </Sorter>
        ),
        render: (_, { due_date, due_date_status }) => (
          <div
            className={cn('td-custom-class', {
              'yellow-cells': due_date_status === DueDateStatuses.DUE_SOON,
              'red-cells': due_date_status === DueDateStatuses.OVERDUE
            })}
          >
            {due_date ? moment(due_date).format(timelineDateFormat) : '-'}
          </div>
        ),
        sorter: true,
        showSorterTooltip: false
      },
      {
        dataIndex: 'total',
        key: 'total',
        className: 'vertical-align-middle',
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<CognusAdminData>
            dataIndex="total"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Total, $
          </Sorter>
        ),
        width: 145,
        render: (_, { total }) => currencyFormatter(total),
        sorter: true,
        showSorterTooltip: false
      },
      {
        dataIndex: 'invoices',
        key: 'invoices',
        title: canSeeInvoices ? 'Invoices' : 'Remittance',
        className: 'vertical-align-middle',
        width: 70,
        render: (_, { uuid, led_by }) => {
          const showPayorRecipientPopover = checkShowPayorRecipientPopover(
            userRole,
            canSeeInvoices,
            led_by
          )
          return (
            <Tooltip
              title={`Download the ${
                canSeeInvoices ? 'Invoice' : 'Remit advice'
              } document`}
            >
              {showPayorRecipientPopover ? (
                <DescriptionCell
                  triggerElement={
                    <Button.Icon
                      type={BUTTON_TYPES.DEFAULT}
                      medium
                      icon={<Download16 />}
                    />
                  }
                  content={
                    <SelectPayorRecipientPopoverContent
                      userRole={userRole}
                      onDownload={(selectedLedBy) =>
                        generateDocument(uuid, canSeeInvoices, selectedLedBy)
                      }
                      ledBy={led_by}
                    />
                  }
                  title={
                    <div className="column gap-16">
                      {`Download ${
                        canSeeInvoices ? 'invoice' : 'remit advice'
                      }`}
                      <Typography.Body2 className="grey-text">
                        Choose a
                        {canSeeInvoices ? 'n invoice' : ' remittance advice'} to
                        download
                      </Typography.Body2>
                    </div>
                  }
                />
              ) : (
                <Button.Icon
                  type={BUTTON_TYPES.DEFAULT}
                  medium
                  icon={<Download16 />}
                  onClick={() => generateDocument(uuid, canSeeInvoices)}
                />
              )}
            </Tooltip>
          )
        },
        sorter: false,
        showSorterTooltip: false
      }
    ],
    canSeeInvoices
      ? []
      : [
          {
            dataIndex: 'payor',
            key: 'payor',
            className: 'vertical-align-middle',
            title: ({ sortOrder, sortColumn }) => (
              <Sorter<CognusAdminData>
                dataIndex="payor"
                sortColumn={sortColumn}
                sortOrder={sortOrder as SortOrder}
              >
                Payor
              </Sorter>
            ),
            width: 205,
            render: (_, { payor, led_by, is_indicator_enabled }) => (
              <div className="row gap-8 align-center">
                {is_indicator_enabled && led_by && (
                  <LeadByIndicator
                    value={led_by}
                    tooltipTitle={
                      userRole === ROLES.VICE_PRESIDENT ? payor : undefined
                    }
                  />
                )}
                {userRole !== ROLES.VICE_PRESIDENT && (
                  <Typography.Body2>{payor}</Typography.Body2>
                )}
              </div>
            ),
            sorter: true,
            showSorterTooltip: false,
            filterDropdown: (props) => (
              <Dropdown.Filter<
                CognusAdminDataFilters,
                CognusAdminFilterFunc,
                unknown
              >
                applyFilters={applyFilters}
                appliedFilters={filters}
                searchCallback={getPayorFilterOptions}
                field="payors"
                optionFormatter={optionFormatter}
                asyncSearch
                {...props}
              />
            ),
            filterIcon: filters?.payors?.length ? (
              <FilterApplied16 />
            ) : (
              <Filter16 />
            ),
            filterSearch: true
          }
        ],
    [
      {
        dataIndex: 'status',
        key: 'status',
        className: 'vertical-align-middle',
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<CognusAdminData>
            dataIndex="status"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Status
          </Sorter>
        ),
        width: 145,
        render: (_, { status }) => (
          <Status.Default
            className={FINANCIAL_STATUS_TYPES_TO_CLASSNAME[status]}
          >
            {FINANCIAL_STATUS_LABEL[status]}
          </Status.Default>
        ),
        sorter: true,
        showSorterTooltip: false
      }
    ]
  )
}
